import { forwardRef, type ReactNode } from 'react'
import * as S from '@radix-ui/react-select'
import { Check, ChevronDown } from 'lucide-react'
import { cn } from './cn'
import { useFieldControl } from './field'

export interface SelectOption<V extends string = string> { value: V; label: ReactNode; description?: ReactNode; icon?: ReactNode; disabled?: boolean }
export interface SelectGroup<V extends string = string> { label: ReactNode; options: SelectOption<V>[] }

export interface SelectProps<V extends string = string> {
  value: V | null
  onValueChange: (v: V) => void
  /** Flat options or labelled groups. */
  options: Array<SelectOption<V> | SelectGroup<V>>
  placeholder?: string
  disabled?: boolean
  invalid?: boolean
  size?: 'sm' | 'md' | 'lg'
  id?: string
  name?: string
  className?: string
  /** Popup width follows the trigger (default) or its content. */
  contentWidth?: 'trigger' | 'auto'
  'aria-label'?: string
}

const isGroup = <V extends string>(o: SelectOption<V> | SelectGroup<V>): o is SelectGroup<V> => 'options' in o

/**
 * Native-feeling select (Radix): typeahead, keyboard, scroll buttons. For long or searchable lists
 * use Combobox. `<Select value={v} onValueChange={setV} options={[{value:'kg',label:'Kilograms'}]} />`
 */
export const Select = forwardRef<HTMLButtonElement, SelectProps>(function Select(
  { value, onValueChange, options, placeholder = 'Select…', disabled, invalid, size = 'md', id, name, className, contentWidth = 'trigger', ...aria }, ref,
) {
  const f = useFieldControl()
  const isInvalid = invalid ?? f?.invalid ?? false
  const renderOption = (o: SelectOption) => (
    <S.Item key={o.value} value={o.value} disabled={o.disabled}
      className="relative flex cursor-default select-none items-center gap-2 rounded-[6px] min-h-8 py-1 pr-8 pl-2 text-base text-text outline-none data-[highlighted]:bg-surface-active data-[disabled]:opacity-50 [&_svg]:size-4">
      {o.icon && <span className="text-text-muted">{o.icon}</span>}
      <span className="flex min-w-0 flex-col">
        <S.ItemText>{o.label}</S.ItemText>
        {o.description && <span className="text-sm text-text-muted">{o.description}</span>}
      </span>
      <S.ItemIndicator className="absolute right-2 text-primary-strong"><Check /></S.ItemIndicator>
    </S.Item>
  )
  return (
    <S.Root value={value ?? undefined} onValueChange={onValueChange} disabled={disabled ?? f?.disabled} name={name}>
      <S.Trigger
        ref={ref}
        id={id ?? f?.id}
        aria-invalid={isInvalid || undefined}
        aria-describedby={f?.describedBy}
        aria-label={aria['aria-label']}
        className={cn(
          'group flex w-full min-w-0 items-center justify-between gap-2 rounded-control border border-input bg-surface px-3 text-left text-text shadow-control outline-none',
          'transition-[border-color,box-shadow] duration-150 hover:border-border-strong focus-visible:border-focus focus-visible:ring-3 focus-visible:ring-focus/20',
          'data-[state=open]:border-focus data-[state=open]:ring-3 data-[state=open]:ring-focus/20 aria-[invalid=true]:border-danger',
          'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-text-subtle data-[placeholder]:text-text-subtle',
          size === 'sm' ? 'h-7 text-sm' : size === 'lg' ? 'h-10 text-base' : 'h-8 text-base',
          className,
        )}
      >
        <span className="truncate"><S.Value placeholder={placeholder} /></span>
        <S.Icon asChild><ChevronDown className="size-4 shrink-0 text-text-subtle transition-transform duration-150 group-data-[state=open]:rotate-180" /></S.Icon>
      </S.Trigger>
      <S.Portal>
        <S.Content
          position="popper"
          sideOffset={4}
          collisionPadding={8}
          className={cn(
            'z-[60] max-h-[min(var(--radix-select-content-available-height),340px)] overflow-hidden rounded-card border border-border bg-surface-raised shadow-popover',
            'data-[state=open]:animate-pop-in data-[state=closed]:animate-pop-out',
            contentWidth === 'trigger' ? 'w-[var(--radix-select-trigger-width)] min-w-40' : 'min-w-[var(--radix-select-trigger-width)]',
          )}
        >
          <S.ScrollUpButton className="flex h-6 items-center justify-center text-text-subtle"><ChevronDown className="size-4 rotate-180" /></S.ScrollUpButton>
          <S.Viewport className="p-1">
            {options.map((o, i) => isGroup(o) ? (
              <S.Group key={i} className={cn(i > 0 && 'mt-1 border-t border-border pt-1')}>
                <S.Label className="px-2 pt-2 pb-1 text-xs font-medium text-text-subtle">{o.label}</S.Label>
                {o.options.map(renderOption)}
              </S.Group>
            ) : renderOption(o))}
          </S.Viewport>
          <S.ScrollDownButton className="flex h-6 items-center justify-center text-text-subtle"><ChevronDown className="size-4" /></S.ScrollDownButton>
        </S.Content>
      </S.Portal>
    </S.Root>
  )
}) as <V extends string = string>(p: SelectProps<V> & { ref?: React.Ref<HTMLButtonElement> }) => React.ReactElement
